import * as React from 'react';
import Styled, { css } from 'styled-components';

import { media } from '../../lib/StyledComponents';
import { SectionHeader } from '../Header';
import { DatapointLabel, Label } from '../Typography';

const DEFAULT_MAX_COLUMNS: number = 4;

interface IDatapointGrid {
  rows: IGridRow[];
  headline?: string;
  placeholder?: JSX.Element;
  maxColumns?: number;
}

interface IInfoGrid {
  maxColumns?: number;
}

export interface IGridRow {
  label: string;
  value?: string | JSX.Element | null;
  position?: number;
  span?: number;
}

interface IPositionedCell {
  position?: number;
  span?: number;
}

const DefaultPlaceholder: React.FC = () => (
  <div>&mdash;</div>
);

// tslint:disable: no-magic-numbers
export const InfoGrid = Styled.div<IInfoGrid>`
  /* IE11 Support */
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 0 1em;
  width: 100%;
  margin: 1.5em 0;
  font-size: 1.4rem;

  @supports (display: grid) {
    display: grid;
    grid-template-columns: 100%;
    grid-auto-rows: auto;
    grid-gap: 1.5em 1em;

    ${({ maxColumns }) => css`
      ${media.small`
        grid-template-columns: ${maxColumns || DEFAULT_MAX_COLUMNS < 2 ?
          `repeat(${maxColumns || DEFAULT_MAX_COLUMNS}, 1fr)` :
          'repeat(2, 1fr)'};
      `}
      ${media.medium`
        grid-template-columns: ${maxColumns || DEFAULT_MAX_COLUMNS < 3 ?
          `repeat(${maxColumns || DEFAULT_MAX_COLUMNS}, 1fr)` :
          'repeat(3, 1fr)'};
      `}
      ${media.large`
        grid-template-columns: ${maxColumns || DEFAULT_MAX_COLUMNS < 4 ?
          `repeat(${maxColumns || DEFAULT_MAX_COLUMNS}, 1fr)` :
          'repeat(4, 1fr)'};
      `}
    `}
  }
`;
InfoGrid.defaultProps = {
  maxColumns: 4,
};

export const PositionedCell = Styled.div<IPositionedCell>`
  grid-column-start: ${({ position }) => position ? position : 'auto'};
  grid-column-end: span 1;
  overflow: hidden;

  span,
  div {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${({ span }) => span && css`
    ${media.small`
      grid-column-end: ${span > 2 ? 'span 2' : `span ${span}`};
    `}
    ${media.medium`
      grid-column-end: ${span > 3 ? 'span 3' : `span ${span}`};
    `}
    ${media.large`
      grid-column-end: ${span > 4 ? 'span 4' : `span ${span}`};
    `}
  `}
`;
// tslint:enable: no-magic-numbers

export const DatapointGrid: React.FC<IDatapointGrid> = ({
  headline,
  rows,
  placeholder,
  maxColumns,
  ...rest
}) => (
  <>
    {headline && (
      <SectionHeader>
        <Label>{headline}</Label>
      </SectionHeader>
    )}
    <InfoGrid {...rest} maxColumns={maxColumns || DEFAULT_MAX_COLUMNS}>
      {rows.map(row => (
        <PositionedCell key={row.label} position={row.position} span={row.span}>
          <DatapointLabel>{row.label}</DatapointLabel>
          <div>{row.value || placeholder || <DefaultPlaceholder />}</div>
        </PositionedCell>
      ))}
    </InfoGrid>
  </>
);
